import {original, produce} from 'immer';

// This function is called with two different query result types — BucketedQueryResult
// (run has `bucketedHistory`) and BackendLinesQueryResult (run has `lines`). Their run
// shapes differ, so neither works as a shared parameter type. AnyDeltaQueryResult also
// doesn't work because the grouped variants lack `runs` entirely. A hand-written
// structural type scoped to the three fields this function actually reads is the
// simplest correct solution.
interface DeltaShape {
  order?: string[] | null;
  delta?: Array<{run?: {id: string} | null} | null> | null;
  incomplete?: string[] | null;
}
interface PinnedSubQueryResult {
  data?: {
    project?: {
      runs?: {deltas?: DeltaShape | null} | null;
      pinnedRuns?: {deltas?: DeltaShape | null} | null;
    } | null;
  } | null;
}

/**
 * Applies the pinnedRuns sub-query result into the main runs result in place,
 * prepending pinned/baseline runs to the front of the order and delta arrays.
 *
 * The pinnedRuns field is fetched via a GraphQL alias on the same Project type,
 * filtered to the pinned/baseline run names only.
 *
 * Visibility is already enforced at query time: the pinnedRunFilters variable
 * ANDs the pinned-names filter with the user's selections filter, so hidden
 * pinned runs are simply not fetched and won't appear here.
 *
 * Note: baseline runs are included in the pinnedRuns sub-query (they share the
 * same filter variable), so there is no separate baselineRun alias as in older
 * versions of this code. The isBaseline / isPinned flags on rendered lines are
 * computed independently in useBucketedData.ts from the run IDs.
 */
export function applyPinnedSubQuery(result: null | undefined): null | undefined;
export function applyPinnedSubQuery<T extends PinnedSubQueryResult>(
  result: T
): T;
export function applyPinnedSubQuery<T extends PinnedSubQueryResult>(
  result: T | null | undefined
): T | null | undefined {
  if (!result) {
    return result;
  }

  return produce(result, draft => {
    const mainDeltas = draft.data?.project?.runs?.deltas;
    const pinnedDeltas = draft.data?.project?.pinnedRuns?.deltas
      ? original(draft.data?.project?.pinnedRuns?.deltas)
      : null;

    if (!mainDeltas || !pinnedDeltas) {
      return;
    }

    const pinnedOrder: string[] = pinnedDeltas.order ?? [];
    if (pinnedOrder.length === 0) {
      return;
    }

    mainDeltas.order = [...pinnedOrder, ...(mainDeltas.order ?? [])];
    mainDeltas.delta = [
      ...(pinnedDeltas.delta ?? []),
      ...(mainDeltas.delta ?? []),
    ];
    delete draft.data?.project?.pinnedRuns;

    // Propagate incomplete run IDs from the pinned sub-query so the follow-up
    // fetch logic in bucketedDeltaQuery.ts handles them the same way as
    // incomplete regular runs.
    if (pinnedDeltas.incomplete?.length) {
      mainDeltas.incomplete = [
        ...pinnedDeltas.incomplete,
        ...(mainDeltas.incomplete ?? []),
      ];
    }
  });
}
